import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useApplyMerge, mergeConflicts } from '../contexts/merge';

// Confirmación de una unificación (admin-merge.md). Acción IRREVERSIBLE → doble barrera:
// checkbox de reconocimiento + botón peligro. Enfoca "Cancelar" por defecto (useModalDialog).
export default function MergeConfirmModal({ entity, noun, base, absorbed, preview, labelOf, onClose, onConfirmed }) {
    const cardRef = useModalDialog(onClose);
    const { mutateAsync: apply, isPending: busy } = useApplyMerge();
    const [ack, setAck] = useState(false);

    const affected = preview?.affected ?? {};
    const affectedTotal = Object.values(affected).reduce((a, n) => a + (Number(n) || 0), 0);
    const released = Object.entries(preview?.released_uniques ?? {});

    const submit = async () => {
        if (!ack || busy) return;
        try {
            await apply({ entity, baseId: base.id, absorbedId: absorbed.id });
            toast.success(`Unificado: «${labelOf(absorbed)}» → «${labelOf(base)}»`);
            onConfirmed?.();
        } catch (er) {
            if (er?.status === 409 && mergeConflicts(er).length) toast.error('Aparecieron conflictos; volvé a previsualizar.');
            else if (er?.status === 404) toast.error('Uno de los registros ya no existe.');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo unificar');
        }
    };

    return (
        <div className="mrg-modal" role="dialog" aria-modal="true" aria-labelledby="mrg-modal-title">
            <div className="mrg-modal__overlay" onClick={busy ? undefined : onClose} aria-hidden="true" />
            <div className="mrg-modal__card" ref={cardRef}>
                <header className="mrg-modal__head">
                    <h2 className="mrg-modal__title" id="mrg-modal-title">Unificar {noun}</h2>
                    <button type="button" className="mrg-modal__close" onClick={onClose} disabled={busy} aria-label="Cancelar"><IconClose /></button>
                </header>

                <div className="mrg-flow">
                    <div className="mrg-flow__node mrg-flow__node--absorb">
                        <span className="mrg-flow__eyebrow">Se apaga</span>
                        <span className="mrg-flow__name">{labelOf(absorbed)}</span>
                    </div>
                    <span className="mrg-flow__arrow" aria-hidden="true">→</span>
                    <div className="mrg-flow__node mrg-flow__node--base">
                        <span className="mrg-flow__eyebrow">Persiste</span>
                        <span className="mrg-flow__name">{labelOf(base)}</span>
                    </div>
                </div>

                <p className="mrg-modal__summary">
                    Se repuntarán <strong>{affectedTotal}</strong> referencia(s) a «{labelOf(base)}» y «{labelOf(absorbed)}» quedará dada de baja.
                </p>
                {released.length > 0 && (
                    <p className="mrg-modal__released">Libera: {released.map(([k, v]) => `${k} (${v})`).join(' · ')}</p>
                )}

                <div className="mrg-modal__warn" role="note">
                    <strong>Acción irreversible.</strong> No existe «deshacer»: la única recuperación es manual, desde la bitácora de auditoría.
                </div>

                <label className="mrg-modal__ack">
                    <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={busy} />
                    <span>Entiendo que es irreversible y confirmo la unificación.</span>
                </label>

                <footer className="mrg-modal__foot">
                    <button type="button" className="mrg-btn mrg-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="mrg-btn mrg-btn--danger" onClick={submit} disabled={busy || !ack}>{busy ? 'Unificando…' : 'Unificar definitivamente'}</button>
                </footer>
            </div>
        </div>
    );
}
